'use client';

import type { ReactNode } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Copy01Icon, InboxIcon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Clipboard } from '../../components/clipboard';
import { IconButton } from '../../components/icon-button';
import { FieldWrapper } from '../../components/field-wrapper';
import { BodyDefault, MonoDefault } from '../../components/typography';

type GeneratedMailboxSectionProps = {
  emailAddress: string;
  children?: ReactNode;
};

export function GeneratedMailboxSection({
  emailAddress,
  children,
}: GeneratedMailboxSectionProps) {
  const t = useTranslations('designSystem.applicationCredentials');
  const tEmail = useTranslations('designSystem.mfa.emailDisplay');

  return (
    <VStack alignItems="stretch" gap={3} py={2.5}>
      <HStack gap={2}>
        <InboxIcon size={16} />
        <BodyDefault fontWeight="medium">{t('generatedMailbox')}</BodyDefault>
      </HStack>
      <FieldWrapper label={tEmail('generatedEmailAddress')}>
        <HStack gap={1} alignItems="center" alignSelf="flex-start">
          <MonoDefault
            px="2"
            py="1"
            bg="bg.neutral.subtle"
            borderRadius="sm"
            color="fg.neutral.default"
          >
            {emailAddress}
          </MonoDefault>
          <Clipboard.Root value={emailAddress}>
            <Clipboard.Trigger asChild>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={tEmail('copyEmailAddress')}
              >
                <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                  <Copy01Icon size={16} />
                </Clipboard.Indicator>
              </IconButton>
            </Clipboard.Trigger>
          </Clipboard.Root>
        </HStack>
      </FieldWrapper>
      {children}
    </VStack>
  );
}
